/**
 * Handles taps on push notifications: routes to the same destination the in-app
 * dropdown uses. Covers both warm taps (app running) and cold-start taps (app
 * launched by the notification).
 */
import { useEffect, useRef } from 'react'
import { Platform } from 'react-native'
import { useRouter, useRootNavigationState } from 'expo-router'
import * as Notifications from 'expo-notifications'
import { buildNotificationRoute } from '../helpers/notificationRoute.helper'
import type { PushNotificationData } from '../types/notifications.types'

function routeForResponse(response: Notifications.NotificationResponse | null): string | null {
  if (!response) return null
  const data = response.notification.request.content.data as PushNotificationData | undefined
  return buildNotificationRoute(data?.type, data?.linkId)
}

export function usePushNotificationResponse(): void {
  const router = useRouter()
  // undefined until the root navigator has mounted — gate cold-start nav on it.
  const rootNavigationState = useRootNavigationState()
  const isRouterReady = Boolean(rootNavigationState?.key)
  const coldStartHandledRef = useRef(false)

  // Warm taps: the router is already mounted, navigate immediately.
  useEffect(() => {
    if (Platform.OS === 'web') return
    const sub = Notifications.addNotificationResponseReceivedListener((response) => {
      const route = routeForResponse(response)
      if (route) router.push(route as `/${string}`)
    })
    return () => sub.remove()
  }, [router])

  // Cold start: the app was launched by tapping a notification. Wait for the
  // router to be ready or the push would silently no-op.
  useEffect(() => {
    if (Platform.OS === 'web') return
    if (!isRouterReady || coldStartHandledRef.current) return
    coldStartHandledRef.current = true
    void Notifications.getLastNotificationResponseAsync().then((response) => {
      const route = routeForResponse(response)
      if (route) router.push(route as `/${string}`)
    })
  }, [isRouterReady, router])
}
